/**
 * Where the bill viewer's "Extracted fields" panel reads its payload from.
 *
 * Two writers, two shapes:
 *  - the OCR worker stores the validated `BillExtraction` **flat**
 *    (`{vendor, amount, currency, …}`);
 *  - the Spendings importer already had the numbers, so its `--attach-bills`
 *    run wraps them: `{importer, source, masterRowNo, files, extraction: {…},
 *    attachments: […]}`. Reading the wrapper flat finds no fields at all —
 *    which is exactly how a CONFIRMED bill could end up labelled "Nothing was
 *    extracted".
 */

export type OcrJson = Record<string, unknown>;

/** The importer's wrapper key holding the actual extraction. */
const EXTRACTION_KEY = 'extraction';

/** Statuses whose bill row is expected to carry a payload worth showing. */
export const EXTRACTED_STATUSES = ['EXTRACTED', 'CONFIRMED'] as const;

export type ExtractedStatus = (typeof EXTRACTED_STATUSES)[number];

function isOcrJson(value: unknown): value is OcrJson {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Unwraps the importer's nested `extraction`, passes a flat payload through and
 * returns `null` for anything that is not an object (the API maps `DbNull` to
 * `null`).
 */
export function unwrapExtraction(json: unknown): OcrJson | null {
  if (!isOcrJson(json)) return null;
  const nested = json[EXTRACTION_KEY];
  return isOcrJson(nested) ? nested : json;
}

/**
 * The panel's data source: the `BillFile.ocrJson` row, falling back to the
 * linked transaction's mirror only when the file row has no payload at all.
 *
 * The OCR worker writes both rows, but the importer only mirrors onto the
 * leg's transaction, so a document attached to a leg *without* a payload stays
 * payload-less. The API's `BillFileView` does not embed the transaction's
 * `ocrJson`, so a caller passes `transactionJson` only when it already holds it
 * (a defensive fallback, never an extra request).
 */
export function resolveExtraction(
  billJson: unknown,
  transactionJson?: unknown,
): OcrJson | null {
  return unwrapExtraction(billJson) ?? unwrapExtraction(transactionJson);
}

/** A payload is rendered for EXTRACTED (needs review) and CONFIRMED (reviewed). */
export function hasExtractedFields(status: string | null | undefined, payload: unknown): boolean {
  return (
    payload !== null &&
    EXTRACTED_STATUSES.includes(status as ExtractedStatus)
  );
}

/** The CONFIRMED badge: reviewed by a human (or seeded by the importer). */
export function isConfirmed(status: string | null | undefined): boolean {
  return status === 'CONFIRMED';
}
